import * as React from 'react';
import clsx from 'clsx';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

/**
 * Creates a named context, provider, and hook.
 *
 * @param options create context options
 */

function createContext(options) {
  if (options === void 0) {
    options = {};
  }

  var _a = options.strict,
      strict = _a === void 0 ? true : _a,
      _b = options.errorMessage,
      errorMessage = _b === void 0 ? 'useContext: `context` is undefined. Seems you forgot to wrap component within the Provider' : _b,
      name = options.name;
  var Context = /*#__PURE__*/React.createContext(undefined);
  Context.displayName = name;

  function useContext() {
    var _a;

    var context = React.useContext(Context);

    if (!context && strict) {
      var error = new Error(errorMessage);
      error.name = 'ContextError';
      (_a = Error.captureStackTrace) === null || _a === void 0 ? void 0 : _a.call(Error, error, useContext);
      throw error;
    }

    return context;
  }

  return [Context.Provider, useContext, Context];
}

var _a;
var CollapsibleContextProvider = (_a = createContext(), _a[0]), useCollapsibleContext = _a[1];

function styleInject(css, ref) {
  if (ref === void 0) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') {
    return;
  }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".Collapsible-module_wrapper__IGeYB{--border-color:var(--component-collapsible-default-border-color,var(--color-gray-30));--card-border-radius:var(--component-collapsible-card-default-border-radius,12px);--list-border-radius:0;--list-focused-border-radius:var(--component-collapsible-list-focused-border-radius,0)}.Collapsible-module_wrapper__IGeYB.Collapsible-module_list__YHCDf{border-radius:var(--list-border-radius);border-bottom:1px solid var(--border-color)}.Collapsible-module_wrapper__IGeYB.Collapsible-module_card__2Hevc{border-radius:var(--card-border-radius);border:1px solid var(--border-color)}.Collapsible-module_trigger__syRcz{--trigger-font-weight:var(--component-collapsible-card-trigger-default-font-weight,600);--trigger-font-size:var(--component-collapsible-card-trigger-default-font-size,17px);--trigger-padding:var(--component-collapsible-card-trigger-default-padding,18px 16px);display:flex;align-items:center;transition:all .3s ease-in;transition:background-color .15s ease-in;transition:font-weight .15s ease-in;justify-content:space-between;border:1px solid transparent;width:100%;background:transparent;padding:var(--trigger-padding);font-size:var(--trigger-font-size);font-weight:var(--trigger-font-weight);cursor:pointer}.Collapsible-module_wrapper__IGeYB.Collapsible-module_list__YHCDf .Collapsible-module_trigger__syRcz{border-radius:var(--list-border-radius)}.Collapsible-module_wrapper__IGeYB.Collapsible-module_card__2Hevc .Collapsible-module_trigger__syRcz{border-radius:var(--card-border-radius)}.Collapsible-module_wrapper__IGeYB.Collapsible-module_card__2Hevc:has(.Collapsible-module_trigger__syRcz[aria-expanded=true]){border:1px solid var(--component-collapsible-card-opened-border-color,var(--border-color))}.Collapsible-module_trigger__syRcz[aria-expanded=true]{font-weight:var(--component-collapsible-card-trigger-opened-font-weight,var(--trigger-font-weight))}.Collapsible-module_wrapper__IGeYB.Collapsible-module_card__2Hevc .Collapsible-module_trigger__syRcz[aria-expanded=true]{border-bottom:1px solid var(--component-collapsible-card-trigger-opened-border-bottom-color,var(--border-color));border-radius:0;border-top-right-radius:var(--card-border-radius);border-top-left-radius:var(--card-border-radius)}.Collapsible-module_wrapper__IGeYB.Collapsible-module_list__YHCDf .Collapsible-module_trigger__syRcz[aria-expanded=true][aria-disabled=false]:hover{--background-color:var(--component-collapsible-list-trigger-hovered-background-color,var(--component-collapsible-card-trigger-hovered-background-color,var(--color-gray-5)));border:1px solid var(--component-collapsible-card-trigger-hovered-border-color,var(--border-color));border-radius:var(--list-focused-border-radius);background-color:var(--background-color)}.Collapsible-module_wrapper__IGeYB.Collapsible-module_list__YHCDf .Collapsible-module_trigger__syRcz[aria-expanded=false][aria-disabled=false]:hover{--background-color:var(--component-collapsible-list-trigger-hovered-background-color,var(--component-collapsible-card-trigger-hovered-background-color,var(--color-gray-5)));border-radius:var(--list-focused-border-radius);background-color:var(--background-color);transition:border .15s ease-in}.Collapsible-module_wrapper__IGeYB.Collapsible-module_card__2Hevc .Collapsible-module_trigger__syRcz[aria-expanded=true][aria-disabled=false]:hover{background-color:var(--component-collapsible-card-trigger-hovered-background-color,var(--color-gray-5));border-bottom:1px solid var(--component-collapsible-card-trigger-hovered-border-color,var(--border-color));border:1px solid var(--component-collapsible-card-trigger-hovered-border-color,var(--border-color));border-bottom-color:var(--component-collapsible-card-trigger-opened-border-bottom-color,var(--border-color))}.Collapsible-module_wrapper__IGeYB.Collapsible-module_card__2Hevc .Collapsible-module_trigger__syRcz[aria-expanded=false][aria-disabled=false]:hover{background-color:var(--component-collapsible-card-trigger-hovered-background-color,var(--color-gray-5));border:1px solid var(--component-collapsible-card-hovered-border-color,var(--border-color));transition:border .15s ease-in}.Collapsible-module_wrapper__IGeYB.Collapsible-module_list__YHCDf .Collapsible-module_trigger__syRcz[aria-disabled=false]:active{background-color:var(--component-collapsible-list-trigger-pressed-background-color,var(--color-gray-10))!important;transition:background-color 0ms ease-in}.Collapsible-module_wrapper__IGeYB.Collapsible-module_card__2Hevc .Collapsible-module_trigger__syRcz[aria-disabled=false]:active{background-color:var(--component-collapsible-card-trigger-pressed-background-color,var(--color-gray-10))!important;transition:background-color 0ms ease-in}.Collapsible-module_trigger__syRcz:focus-visible{outline:none}.Collapsible-module_wrapper__IGeYB.Collapsible-module_list__YHCDf:has(.Collapsible-module_trigger__syRcz[aria-disabled=false]:focus-visible){box-shadow:0 0 0 2px var(--color-white),0 0 0 4px var(--color-blue-50);border-radius:var(--list-focused-border-radius)}.Collapsible-module_wrapper__IGeYB.Collapsible-module_card__2Hevc:has(.Collapsible-module_trigger__syRcz[aria-disabled=false]:focus-visible){border:1px solid var(--color-gray-30);box-shadow:0 0 0 2px var(--color-white),0 0 0 4px var(--color-blue-50);border-radius:var(--card-border-radius)}.Collapsible-module_expandableIcon__W-lBF{transition:transform .3s;flex:0 0 30px;color:var(--color-icon-default)}.Collapsible-module_trigger__syRcz[aria-disabled=true]{cursor:not-allowed;background-color:var(--component-collapsible-card-trigger-disabled-background-color,var(--color-surface-subdued));color:var(--component-collapsible-card-trigger-disabled-text-color,var(--color-text-subdued))}.Collapsible-module_trigger__syRcz[aria-disabled=true] .Collapsible-module_expandableIcon__W-lBF{color:var(--color-icon-disabled)}.Collapsible-module_trigger__syRcz[aria-expanded=true] .Collapsible-module_expandableIcon__W-lBF{transform:rotate(180deg)}.Collapsible-module_collapsibleContent__3kLks{overflow:hidden}.Collapsible-module_collapsibleContent__3kLks[data-state=open]{animation:Collapsible-module_slideDown__3cCUy var(--collapsible-animation-duration,.3s) ease}.Collapsible-module_collapsibleContent__3kLks[data-state=closed]{animation:Collapsible-module_slideUp__DxqvT var(--collapsible-animation-duration,.3s) ease}@keyframes Collapsible-module_slideDown__3cCUy{0%{height:0}to{height:var(--collapsible-content-height)}}@keyframes Collapsible-module_slideUp__DxqvT{0%{height:var(--collapsible-content-height)}to{height:0}}";
var styles = {"wrapper":"Collapsible-module_wrapper__IGeYB","list":"Collapsible-module_list__YHCDf","card":"Collapsible-module_card__2Hevc","trigger":"Collapsible-module_trigger__syRcz","expandableIcon":"Collapsible-module_expandableIcon__W-lBF","collapsibleContent":"Collapsible-module_collapsibleContent__3kLks","slideDown":"Collapsible-module_slideDown__3cCUy","slideUp":"Collapsible-module_slideUp__DxqvT"};
styleInject(css_248z);

var CollapsibleChevronIcon = function () {
    return (React.createElement("svg", { className: styles.expandableIcon, width: "16", height: "10", viewBox: "0 0 16 10", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
        React.createElement("path", { d: "M15 1.43994L8 8.43994L1 1.43994", stroke: "#52575C", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" })));
};

var CollapsiblePlusIcon = function () {
    var _a = useCollapsibleContext(), internalIsOpen = _a.internalIsOpen, open = _a.open;
    return (React.createElement("svg", { className: clsx(styles.expandableIcon), width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M2.75 12C2.75 11.4477 3.19772 11 3.75 11H20.25C20.8023 11 21.25 11.4477 21.25 12C21.25 12.5523 20.8023 13 20.25 13H3.75C3.19772 13 2.75 12.5523 2.75 12Z", fill: "currentColor" }),
        React.createElement("path", { style: {
                opacity: internalIsOpen || open ? 0 : 1,
                transition: 'opacity 0.2s',
            }, fillRule: "evenodd", clipRule: "evenodd", d: "M12 2.75C12.5523 2.75 13 3.19772 13 3.75V20.25C13 20.8023 12.5523 21.25 12 21.25C11.4477 21.25 11 20.8023 11 20.25V3.75C11 3.19772 11.4477 2.75 12 2.75Z", fill: "currentColor" })));
};

/* -------------------------------------------------------------------------------------------------
 * Primitive
 * -----------------------------------------------------------------------------------------------*/
var NAME = 'Primitive';
var DEFAULT_TAG = 'div';
var Primitive = React.forwardRef(function (props, forwardedRef) {
    var _a = props.as, Comp = _a === void 0 ? DEFAULT_TAG : _a, primitiveProps = __rest(props, ["as"]);
    return React.createElement(Comp, __assign({}, primitiveProps, { ref: forwardedRef }));
});
Primitive.displayName = NAME;

var COLLAPSIBLE_TRIGGER_NAME = 'CollapsibleTrigger';
var COLLAPSIBLE_TRIGGER_DEFAULT_TAG = 'button';
var CollapsibleTrigger = React.forwardRef(function (_a, ref) {
    var className = _a.className, children = _a.children, _b = _a.as, as = _b === void 0 ? COLLAPSIBLE_TRIGGER_DEFAULT_TAG : _b;
    var _c = useCollapsibleContext(), internalIsOpen = _c.internalIsOpen, setInternalIsOpen = _c.setInternalIsOpen, open = _c.open, onToggle = _c.onToggle, disabled = _c.disabled;
    var onClick = function () {
        if (!disabled) {
            typeof onToggle === 'undefined'
                ? setInternalIsOpen(!internalIsOpen)
                : onToggle(!open);
        }
    };
    return (React.createElement(Primitive, { ref: ref, as: as, onClick: onClick, className: clsx(styles.trigger, className), "aria-expanded": typeof open === 'undefined' ? internalIsOpen : open, "aria-disabled": disabled }, children));
});
CollapsibleTrigger.displayName = COLLAPSIBLE_TRIGGER_NAME;

var COLLAPSIBLE_CONTENT_NAME = 'CollapsibleContent';
var CollapsibleContent = React.forwardRef(function (_a, forwardedRef) {
    var _b;
    var _c, _d;
    var className = _a.className, children = _a.children, as = _a.as;
    var _e = useCollapsibleContext(), internalIsOpen = _e.internalIsOpen, open = _e.open;
    var currentOpen = typeof open === 'undefined' ? internalIsOpen : open;
    var _f = React.useState(true), shouldShowContent = _f[0], setShouldShowContent = _f[1];
    var internalRef = React.useRef(null);
    // @ts-expect-error
    React.useImperativeHandle(forwardedRef, function () { return internalRef.current; });
    var originalStylesRef = React.useRef();
    var _g = React.useState(0), height = _g[0], setHeight = _g[1];
    var isMounted = React.useRef(false);
    var isMountAnimationPreventedRef = React.useRef(internalIsOpen);
    var ANIMATION_DURATION_MS = 300;
    React.useEffect(function () {
        var rAF = requestAnimationFrame(function () { return (isMountAnimationPreventedRef.current = false); });
        return function () { return cancelAnimationFrame(rAF); };
    }, []);
    React.useEffect(function () {
        var node = internalRef.current;
        if (node) {
            originalStylesRef.current = originalStylesRef.current || {
                transitionDuration: node.style.transitionDuration,
                animationName: node.style.animationName,
            };
            // block any animations/transitions so the element renders at its full dimensions
            node.style.transitionDuration = '0s';
            node.style.animationName = 'none';
            // get width and height from full dimensions
            var rect = node.getBoundingClientRect();
            if (rect.height)
                setHeight(rect.height);
            if (!isMounted.current) {
                setShouldShowContent(currentOpen);
                isMounted.current = true;
                return;
            }
            // kick off any animations/transitions that were originally set up if it isn't the initial mount
            if (!isMountAnimationPreventedRef.current) {
                node.style.transitionDuration =
                    originalStylesRef.current.transitionDuration;
                node.style.animationName = originalStylesRef.current.animationName;
            }
            if (currentOpen)
                setShouldShowContent(true);
            setTimeout(function () {
                if (!currentOpen)
                    setShouldShowContent(false);
                // -50ms to prevent a flash of the content when closing
            }, ANIMATION_DURATION_MS - 50);
        }
    }, [currentOpen]);
    return (React.createElement(React.Fragment, null, React.createElement(Primitive, { ref: internalRef, as: as, "data-state": currentOpen ? 'open' : 'closed', style: (_b = {},
            _b['--collapsible-animation-duration'] = "".concat(ANIMATION_DURATION_MS, "ms"),
            _b["--collapsible-content-height"] = "".concat((_d = (height || ((_c = internalRef.current) === null || _c === void 0 ? void 0 : _c.clientHeight))) !== null && _d !== void 0 ? _d : 0, "px"),
            _b), className: clsx(styles.collapsibleContent, className) }, shouldShowContent && children)));
});
CollapsibleContent.displayName = COLLAPSIBLE_CONTENT_NAME;

var Collapsible = function (_a) {
    var _b = _a.defaultOpen, defaultOpen = _b === void 0 ? false : _b, open = _a.open, onToggle = _a.onToggle, _c = _a.disabled, disabled = _c === void 0 ? false : _c, _d = _a.variant, variant = _d === void 0 ? 'list' : _d, className = _a.className, children = _a.children, props = __rest(_a, ["defaultOpen", "open", "onToggle", "disabled", "variant", "className", "children"]);
    var _e = React.useState(defaultOpen), internalIsOpen = _e[0], setInternalIsOpen = _e[1];
    return (React.createElement(CollapsibleContextProvider, { value: {
            internalIsOpen: internalIsOpen,
            setInternalIsOpen: setInternalIsOpen,
            open: open,
            onToggle: onToggle,
            disabled: disabled,
        } },
        React.createElement("div", __assign({}, props, { className: clsx(styles.wrapper, styles[variant], className) }), children)));
};

export { Collapsible, CollapsibleContent, CollapsibleChevronIcon as CollapsibleIcon, CollapsiblePlusIcon, CollapsibleTrigger };
